// The email style page's pure half (W7): the form ↔ request mapping and the lines it shows.
// The server decides coverage and stores the profile; nothing here grades anything.

import type {
  StyleAnswers, StyleAsk, StyleEmailLabel, StyleIntakeRequest, StyleLength, StyleRule, StyleSituation, StyleState,
} from '../api/types.ts';

export const STYLE_SITUATIONS: { key: StyleSituation; label: string }[] = [
  { key: 'cold', label: 'Cold' },
  { key: 'follow_up_no_reply', label: 'Follow-up, no reply' },
  { key: 'playground_user', label: 'Playground user, not enterprise' },
  { key: 'warm_reply', label: 'Warm or inbound reply' },
  { key: 're_engage', label: 'Re-engage' },
  { key: 'meeting', label: 'Booking or after a meeting' },
];

export const STYLE_LENGTHS: { key: StyleLength; label: string }[] = [
  { key: 'shorter', label: 'Shorter than the house style' },
  { key: 'house', label: 'About the house style (120 words)' },
  { key: 'longer', label: 'A bit longer' },
];

// Empty email boxes the page opens with; "Add another" grows it.
export const STARTING_EMAIL_BOXES = 5;

export interface StyleFormEmail {
  text: string;
  situationHint: StyleSituation | '';
}

export interface StyleForm {
  emails: StyleFormEmail[];
  howYouSound: string;
  always: string;
  never: string;
  greeting: string;
  signOff: string;
  defaultAsk: string;
  length: StyleLength | '';
  aiDraft: string;
  rewrite: string;
}

export function situationLabel(key: string | null | undefined): string {
  const found = STYLE_SITUATIONS.find((situation) => situation.key === key);
  return found ? found.label : 'Other';
}

/** "a, b\nc" → ["a", "b", "c"], trimmed, empties and repeats dropped. */
export function splitPhrases(text: string): string[] {
  const phrases: string[] = [];
  for (const piece of text.split(/[\n,]/)) {
    const phrase = piece.trim();
    if (phrase && !phrases.includes(phrase)) phrases.push(phrase);
  }
  return phrases;
}

function emptyEmails(count: number): StyleFormEmail[] {
  const emails: StyleFormEmail[] = [];
  for (let index = 0; index < count; index += 1) emails.push({ text: '', situationHint: '' });
  return emails;
}

/** The form as the server last stored it, with a few empty boxes when nothing is pasted yet. */
export function formFromState(state: StyleState | undefined): StyleForm {
  const answers: StyleAnswers = state?.answers ?? {};
  const emails: StyleFormEmail[] = (state?.emails ?? []).map((email) => ({
    text: email.text,
    situationHint: email.situation_hint ?? '',
  }));
  return {
    emails: emails.length ? emails : emptyEmails(STARTING_EMAIL_BOXES),
    howYouSound: answers.how_you_sound ?? '',
    always: (answers.always ?? []).join(', '),
    never: (answers.never ?? []).join(', '),
    greeting: answers.greeting ?? '',
    signOff: answers.sign_off ?? '',
    defaultAsk: answers.default_ask ?? '',
    length: answers.length ?? '',
    aiDraft: state?.anti_example?.ai_draft ?? '',
    rewrite: state?.anti_example?.rewrite ?? '',
  };
}

/** The whole intake as one replace: the page is the source of truth for what it shows. */
export function intakeRequest(form: StyleForm): StyleIntakeRequest {
  const emails: StyleIntakeRequest['emails'] = [];
  for (const email of form.emails) {
    const text = email.text.trim();
    if (!text) continue;
    emails.push(email.situationHint ? { text, situation_hint: email.situationHint } : { text });
  }
  const answers: StyleAnswers = {
    how_you_sound: form.howYouSound.trim(),
    always: splitPhrases(form.always),
    never: splitPhrases(form.never),
    greeting: form.greeting.trim(),
    sign_off: form.signOff.trim(),
    default_ask: form.defaultAsk.trim(),
  };
  if (form.length) answers.length = form.length;
  const aiDraft = form.aiDraft.trim();
  const rewrite = form.rewrite.trim();
  return {
    emails,
    answers,
    // Both halves or neither: one half alone teaches nothing.
    anti_example: aiDraft && rewrite ? { ai_draft: aiDraft, rewrite } : null,
    replace: true,
  };
}

/** Blocking asks first, in the server's order within each group. */
export function sortedAsks(asks: StyleAsk[]): StyleAsk[] {
  return [...asks.filter((ask) => ask.blocking), ...asks.filter((ask) => !ask.blocking)];
}

/** What Scout made of one pasted email, as one short line. */
export function labelLine(label: StyleEmailLabel | null): string {
  if (!label) return 'Not read yet';
  if (!label.is_theirs) return 'Not counted: reads like a forward or a machine draft';
  if (label.duplicate_of !== null) return `Counted once: same template as email ${label.duplicate_of + 1}`;
  const persona = label.persona ?? 'recipient unclear';
  return `${situationLabel(label.situation)} · ${persona}`;
}

/** Which profile drafts use right now, in a sentence. */
export function profileStatus(state: StyleState | undefined): string {
  if (!state) return '';
  const active = state.active_version;
  const latest = state.latest_version;
  if (active !== null && latest > active) {
    return `Version ${active} is in use. Version ${latest} is ready for you to check.`;
  }
  if (active !== null) return `Version ${active} is in use: your drafts sound like you.`;
  if (latest > 0) return `Version ${latest} is built but not in use yet. Until then, drafts use the house style.`;
  return 'Your drafts use the house style until you set up your own.';
}

// --- where the strategist is, and what to do next (P4-08) ----------------------------------------

export type StyleStage = 'paste' | 'gaps' | 'build' | 'preview' | 'approve' | 'live';

// The progress strip: one entry per thing the strategist does, in order.
export const STYLE_STAGES: { key: StyleStage; label: string }[] = [
  { key: 'paste', label: 'Add emails' },
  { key: 'gaps', label: 'Fill gaps' },
  { key: 'build', label: 'Build' },
  { key: 'preview', label: 'Preview' },
  { key: 'approve', label: 'Use it' },
];

/** The one step the page is waiting on, from the server's state. */
export function styleStage(state: StyleState | undefined): StyleStage {
  if (!state || !state.emails.length) return 'paste';
  const profile = state.latest_profile;
  if (!profile) return state.coverage.ready ? 'build' : 'gaps';
  const previews = state.previews.length ? state.previews : profile.previews;
  if (!previews.length) return 'preview';
  if (state.active_version !== profile.version) return 'approve';
  return 'live';
}

/** What to do next, in one sentence. */
export function nextStepText(stage: StyleStage): string {
  switch (stage) {
    case 'paste':
      return 'Next: paste at least five emails you sent and liked, then save.';
    case 'gaps':
      return 'Next: add what Scout asks for below, or build with what you have.';
    case 'build':
      return 'Next: build your style. It takes about a minute.';
    case 'preview':
      return 'Next: preview three drafts written in your style.';
    case 'approve':
      return 'Next: if the drafts sound like you, use this style. If not, tell Scout what to change.';
    case 'live':
      return 'Done. Scout drafts in your style. Come back any time to add emails or change a rule.';
  }
}

/** Whether a progress-strip step is finished, current or still ahead. */
export function stageState(step: StyleStage, current: StyleStage): 'done' | 'current' | 'ahead' {
  if (current === 'live') return 'done';
  const order = STYLE_STAGES.map((entry) => entry.key);
  // "Fill gaps" is skipped when coverage was enough: it counts as done once building is next.
  const stepIndex = order.indexOf(step);
  const currentIndex = order.indexOf(current);
  if (stepIndex < currentIndex) return 'done';
  return stepIndex === currentIndex ? 'current' : 'ahead';
}

// --- answering Scout's asks inline ------------------------------------------------------------

export type StyleAnswerKey = 'how_you_sound' | 'greeting' | 'sign_off' | 'default_ask' | 'length';

/** What the strategist typed into the boxes under Scout's asks, by ask. */
export interface InlineAdditions {
  emails: StyleFormEmail[];
  answers: Partial<Record<StyleAnswerKey, string>>;
}

/** An ask Scout makes for another email: a missing situation or kind of buyer. */
export function askWantsEmail(ask: StyleAsk): boolean {
  return ask.blocking && (ask.kind === 'situation' || ask.kind === 'persona');
}

/** An ask for one of the short answers, answered in a box under it. */
export function askWantsAnswer(ask: StyleAsk): ask is StyleAsk & { key: StyleAnswerKey } {
  return ask.kind === 'answer' && ['how_you_sound', 'greeting', 'sign_off', 'default_ask', 'length'].includes(ask.key);
}

/** The situation hint a pasted email gets from the ask it answers. */
export function hintForAsk(ask: StyleAsk): StyleSituation | '' {
  const known = STYLE_SITUATIONS.some((situation) => situation.key === ask.key);
  return ask.kind === 'situation' && known ? (ask.key as StyleSituation) : '';
}

/** The form with the inline additions merged in: new emails after the old, answers replaced. */
export function formWithAdditions(form: StyleForm, additions: InlineAdditions): StyleForm {
  const pasted = form.emails.filter((email) => email.text.trim());
  const added = additions.emails.filter((email) => email.text.trim());
  const answers = additions.answers;
  const length = answers.length?.trim() as StyleLength | undefined;
  return {
    ...form,
    emails: [...pasted, ...added],
    howYouSound: answers.how_you_sound?.trim() || form.howYouSound,
    greeting: answers.greeting?.trim() || form.greeting,
    signOff: answers.sign_off?.trim() || form.signOff,
    defaultAsk: answers.default_ask?.trim() || form.defaultAsk,
    length: length && STYLE_LENGTHS.some((entry) => entry.key === length) ? length : form.length,
  };
}

/** Whether anything was typed under the asks. */
export function hasAdditions(additions: InlineAdditions): boolean {
  return additions.emails.some((email) => email.text.trim())
    || Object.values(additions.answers).some((answer) => (answer ?? '').trim());
}

/** A rule with its reason, as the profile lists it. */
export function ruleLine(rule: StyleRule): string {
  return `${rule.rule} — ${rule.reason}`;
}
